import { useState, useMemo, useEffect } from "react";
import { AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import RackDetailModal from "./RackDetailModal";

const fmt = (n) =>
    n === null || n === undefined || Number.isNaN(Number(n))
        ? "-"
        : Number(n).toLocaleString("id-ID");

// sisa hari menuju kedaluwarsa (null kalau tanggal tidak terbaca)
const daysToExpire = (date) => {
    if (!date) return null;
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return null;
    return Math.ceil((d - new Date()) / (1000 * 60 * 60 * 24));
};

// tampilan "Kedaluwarsa" disembunyikan, denah hanya menampilkan isi slot
const SHOW_MODE_TOGGLE = false;

const MODES = [
    { value: "occupancy", label: "Isi slot" },
    { value: "expired", label: "Kedaluwarsa" }
];

export default function RackDimensionHeatmap({
    rack,
    details = [],
    onClose
}) {

    const [mode, setMode] = useState("occupancy");

    // popup detail
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [openDetail, setOpenDetail] = useState(false);

    const totalRows = rack?.total_rows || rack?.rows || 0;
    const totalCols = rack?.total_columns || rack?.columns || 0;

    const filteredDetails = useMemo(() => {
        if (!Array.isArray(details)) return [];
        return details.flatMap((r) => r.details || []);
    }, [details]);

    // kelompokkan item per slot "baris.kolom"
    const slotMap = useMemo(() => {
        const map = {};
        filteredDetails.forEach((d) => {
            if (!d.level_code) return;
            const [r, c] = String(d.level_code).split(".");
            const key = `${Number(r)}.${Number(c)}`;
            (map[key] = map[key] || []).push(d);
        });
        return map;
    }, [filteredDetails]);

    const totalSlots = totalRows * totalCols;
    const usedSlots = Object.keys(slotMap).filter((k) => {
        const [r, c] = k.split(".").map(Number);
        return r >= 1 && r <= totalRows && c >= 1 && c <= totalCols;
    }).length;
    const usedPct = totalSlots > 0 ? Math.round((usedSlots / totalSlots) * 100) : 0;
    const totalQty = filteredDetails.reduce((a, d) => a + Number(d.qty || 0), 0);

    // tutup dengan tombol Esc (kalau popup detail tidak sedang terbuka)
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape" && !openDetail) onClose?.();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose, openDetail]);

    // gaya slot berdasarkan mode
    const slotStyle = (items) => {
        if (!items) {
            return "bg-white border-dashed border-line text-muted hover:border-muted";
        }

        if (mode === "expired") {
            const days = items
                .map((it) => daysToExpire(it.due_date))
                .filter((d) => d !== null);
            const nearest = days.length ? Math.min(...days) : null;

            if (nearest !== null && nearest <= 30) {
                return "bg-red-50 border-red-300 text-red-800 hover:border-red-500";
            }
            if (nearest !== null && nearest <= 90) {
                return "bg-amber-50 border-amber-300 text-amber-900 hover:border-amber-500";
            }
        }

        return "bg-ink-100 border-ink/30 text-ink-700 hover:border-ink";
    };

    const openSlot = (row, col) => {
        const items = slotMap[`${row}.${col}`];
        if (!items) return;
        setSelectedSlot({ row, col, items, level_code: `${row}.${col}` });
        setOpenDetail(true);
    };

    return (
        <>
            <div
                className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4"
                onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}
            >
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="rack-denah-title"
                    className="bg-white rounded-xl w-full max-w-7xl max-h-[92vh] shadow-2xl flex flex-col overflow-hidden"
                >

                    {/* ===== HEADER ===== */}
                    <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-line">
                        <div className="min-w-0">
                            <div className="text-xs text-muted">Denah rak</div>
                            <h2 id="rack-denah-title" className="text-2xl font-semibold text-body leading-tight mt-0.5">
                                {rack?.racks_name || rack?.code}
                            </h2>
                            <div className="text-sm text-muted mt-1">
                                {totalRows} baris × {totalCols} kolom
                                {rack?.warehouse_name ? `, ${rack.warehouse_name}` : ""}
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={onClose}
                            aria-label="Tutup"
                            className="p-2 -mr-2 rounded-md text-muted hover:text-body hover:bg-surface"
                        >
                            <X size={20} />
                        </button>
                    </div>

                    {/* ===== RINGKASAN + MODE ===== */}
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 px-6 py-4 border-b border-line">

                        <dl className="flex flex-wrap items-center gap-x-8 gap-y-3">
                            <div>
                                <dt className="text-xs text-muted">Slot terisi</dt>
                                <dd className="text-lg font-semibold tabular-nums">
                                    {fmt(usedSlots)} <span className="text-sm font-normal text-muted">/ {fmt(totalSlots)}</span>
                                </dd>
                            </div>
                            <div>
                                <dt className="text-xs text-muted">Slot kosong</dt>
                                <dd className="text-lg font-semibold tabular-nums">{fmt(Math.max(totalSlots - usedSlots, 0))}</dd>
                            </div>
                            <div>
                                <dt className="text-xs text-muted">Total qty</dt>
                                <dd className="text-lg font-semibold tabular-nums">{fmt(totalQty)}</dd>
                            </div>
                            <div className="min-w-[160px]">
                                <dt className="text-xs text-muted">Pemakaian {usedPct}%</dt>
                                <dd className="mt-2 h-2 rounded-full bg-[#E8EEF7] overflow-hidden">
                                    <div
                                        className={`h-full rounded-full ${usedPct >= 100 ? "bg-red-500" : usedPct >= 70 ? "bg-tag" : "bg-ink"}`}
                                        style={{ width: `${Math.min(usedPct, 100)}%` }}
                                    />
                                </dd>
                            </div>
                        </dl>

                        {/* pilihan tampilan disembunyikan; set SHOW_MODE_TOGGLE = true untuk memunculkan lagi */}
                        {SHOW_MODE_TOGGLE && (
                        <div
                            className="inline-flex self-start lg:self-auto bg-surface border border-line rounded-md p-1"
                            role="radiogroup"
                            aria-label="Tampilan"
                        >
                            {MODES.map((m) => (
                                <button
                                    key={m.value}
                                    type="button"
                                    role="radio"
                                    aria-checked={mode === m.value}
                                    onClick={() => setMode(m.value)}
                                    className={`px-3.5 h-8 text-sm rounded transition-colors ${
                                        mode === m.value
                                            ? "bg-white text-ink font-semibold shadow-sm"
                                            : "text-muted hover:text-body"
                                    }`}
                                >
                                    {m.label}
                                </button>
                            ))}
                        </div>
                        )}
                    </div>

                    {/* ===== GRID ===== */}
                    <div className="flex-1 overflow-auto bg-surface px-6 py-5">

                        {totalSlots === 0 ? (
                            <div className="text-center text-sm text-muted py-16">
                                Ukuran rak belum diatur. Isi jumlah baris dan kolom di Master Rak.
                            </div>
                        ) : (
                            <div
                                className="grid gap-2"
                                style={{
                                    gridTemplateColumns: `40px repeat(${totalCols}, minmax(112px, 1fr))`
                                }}
                            >
                                {/* header kolom */}
                                <div />
                                {Array.from({ length: totalCols }).map((_, c) => (
                                    <div key={`h-${c}`} className="text-center text-xs text-muted pb-1">
                                        Kolom {c + 1}
                                    </div>
                                ))}

                                {Array.from({ length: totalRows }).map((_, r) => {
                                    const row = r + 1;
                                    return [
                                        <div
                                            key={`r-${row}`}
                                            className="flex items-center justify-center text-xs text-muted"
                                            title={`Baris ${row}`}
                                        >
                                            {row}
                                        </div>,

                                        ...Array.from({ length: totalCols }).map((__, c) => {
                                            const col = c + 1;
                                            const items = slotMap[`${row}.${col}`];
                                            const first = items?.[0];
                                            const qty = items?.reduce((a, it) => a + Number(it.qty || 0), 0) || 0;
                                            const palets = items
                                                ? new Set(items.map((it) => it.kode_palet).filter(Boolean)).size
                                                : 0;
                                            const nearest = items
                                                ? Math.min(...items.map((it) => daysToExpire(it.due_date)).filter((d) => d !== null), Infinity)
                                                : Infinity;

                                            return (
                                                <button
                                                    key={`${row}-${col}`}
                                                    type="button"
                                                    disabled={!items}
                                                    onClick={() => openSlot(row, col)}
                                                    aria-label={`Slot ${row}.${col}${items ? `, ${items.length} item` : ", kosong"}`}
                                                    className={`text-left rounded-lg border min-h-[96px] p-2.5 flex flex-col transition-colors disabled:cursor-default ${slotStyle(items)}`}
                                                >
                                                    <div className="flex items-center justify-between gap-1">
                                                        <span className="text-xs font-semibold tabular-nums">{row}.{col}</span>
                                                        {items && items.length > 1 && (
                                                            <span className="text-[10px] font-semibold rounded bg-white/80 px-1">
                                                                {items.length} item
                                                            </span>
                                                        )}
                                                    </div>

                                                    {items ? (
                                                        <div className="mt-auto pt-2 min-w-0">
                                                            <div className="text-xs font-semibold truncate" title={first?.name_product || first?.sku}>
                                                                {first?.name_product || first?.sku || "-"}
                                                            </div>
                                                            <div className="text-[11px] opacity-80 truncate tabular-nums">
                                                                {first?.sku}
                                                            </div>
                                                            <div className="flex justify-between text-[11px] mt-1 tabular-nums">
                                                                <span>{palets > 0 ? `${palets} palet` : "tanpa palet"}</span>
                                                                <span className="font-semibold">{fmt(qty)}</span>
                                                            </div>
                                                            {mode === "expired" && Number.isFinite(nearest) && (
                                                                <div className="text-[11px] mt-0.5 font-semibold">
                                                                    {nearest < 0 ? "Sudah kedaluwarsa" : `${nearest} hari lagi`}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <div className="mt-auto text-[11px]">Kosong</div>
                                                    )}
                                                </button>
                                            );
                                        })
                                    ];
                                })}
                            </div>
                        )}
                    </div>

                    {/* ===== LEGEND ===== */}
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 px-6 py-3 border-t border-line text-xs text-muted">
                        <span className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded border border-ink/30 bg-ink-100" /> Terisi
                        </span>
                        <span className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded border border-dashed border-line bg-white" /> Kosong
                        </span>
                        {mode === "expired" && (
                            <>
                                <span className="flex items-center gap-2">
                                    <span className="w-3 h-3 rounded border border-amber-300 bg-amber-50" /> Kedaluwarsa ≤ 90 hari
                                </span>
                                <span className="flex items-center gap-2">
                                    <span className="w-3 h-3 rounded border border-red-300 bg-red-50" /> Kedaluwarsa ≤ 30 hari
                                </span>
                            </>
                        )}
                        <span className="ml-auto hidden sm:inline">Klik slot yang terisi untuk melihat isinya</span>
                    </div>
                </div>
            </div>

            {/* DETAIL POPUP */}
            <AnimatePresence>
                {openDetail && (
                    <RackDetailModal
                        rack={selectedSlot}
                        rackName={rack?.racks_name || rack?.code}
                        data={selectedSlot?.items || []}
                        onClose={() => {
                            setOpenDetail(false);
                            setSelectedSlot(null);
                        }}
                    />
                )}
            </AnimatePresence>
        </>
    );
}
